import React, { createContext, useContext, useState } from 'react'
import { Desktop, DeviceMobile } from '@phosphor-icons/react'

// Define the device type enum if not already imported
export enum DeviceType {
  MOBILE = 'mobile',
  DESKTOP = 'desktop'
}

// Create a context to share device type across components
const PreviewContext = createContext<{
  device: DeviceType
  setDevice: (device: DeviceType) => void
}>({
  device: DeviceType.MOBILE,
  setDevice: () => {}
})

interface PreviewLayoutProps {
  children: React.ReactNode
}

interface PreviewProps {
  children: React.ReactNode
  viewType?: DeviceType
  onBackgroundClick?: () => void
  title?: string
}

interface SetupProps {
  children: React.ReactNode
}

interface PreviewContainerProps {
  device: DeviceType
  children: React.ReactNode
}

// Component for Mobile Preview
const MobilePreview: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div className='h-[740px] w-[360px] bg-white border-8 border-gray-light rounded-[30px] box-border overflow-y-scroll scrollbar-hide'>
      {children}
    </div>
  )
}

// Component for Desktop Preview
const DesktopPreview: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Fixed scale at 45% for optimal viewing
  const fixedScale = 0.7

  return (
    <div className='h-[500px] w-full overflow-hidden relative bg-gray-lightest'>
      <div
        className='origin-top-left absolute top-0 left-0 bg-white border-2 border-gray-light rounded-lg box-border'
        style={{
          transform: `scale(${fixedScale})`,
          width: `${100 / fixedScale}%`,
          height: `${100 / fixedScale}%`,
          transformOrigin: 'top left',
          overflow: 'auto'
        }}
      >
        {children}
      </div>
    </div>
  )
}

// Container that renders either mobile or desktop preview based on context
export const PreviewContainer: React.FC<PreviewContainerProps> = ({ device, children }) => {
  return device === DeviceType.MOBILE ? (
    <MobilePreview>{children}</MobilePreview>
  ) : (
    <DesktopPreview>{children}</DesktopPreview>
  )
}

const Preview: React.FC<PreviewProps> = ({ children, viewType, onBackgroundClick, title }) => {
  const { device, setDevice } = useContext(PreviewContext)

  const screenSetter = (
    <div className='flex gap-2 bg-coal-lighter p-1 rounded-md'>
      <div
        className={`px-2 py-1 transition-all duration-200 ${device === DeviceType.MOBILE ? 'bg-white' : ''}`}
        onClick={() => {
          setDevice(DeviceType.MOBILE)
        }}
      >
        <DeviceMobile size={20} className='cursor-pointer' />
      </div>
      <div
        className={`px-2 py-1 transition-all duration-200 ${device === DeviceType.DESKTOP ? 'bg-white' : ''}`}
        onClick={() => {
          setDevice(DeviceType.DESKTOP)
        }}
      >
        <Desktop size={20} className='cursor-pointer' />
      </div>
    </div>
  )

  return (
    <div className='flex flex-col w-full'>
      {title && <h3 className='text-text-lg-semibold text-zinc-600 px-6 py-3 pt-5'>{title}</h3>}
      <div
        className='flex-1 flex flex-col p-6 sticky max-h-[calc(100vh-40px)] overflow-auto bg-gray-50 bg-[image:radial-gradient(circle,rgb(209_213_219)_1px,transparent_1px)] [background-size:20px_20px]'
        onClick={onBackgroundClick}
      >
        {!viewType && (
          <div className='flex justify-end'>
            <div className='flex flex-col gap-2'>
              <h2 className='text-sm font-semibold'>Device</h2>
              {screenSetter}
            </div>
          </div>
        )}
        <div className='flex-1 flex justify-center items-start mt-4'>
          <PreviewContainer device={viewType ?? device}>{children}</PreviewContainer>
        </div>
      </div>
    </div>
  )
}

const Setup: React.FC<SetupProps> = ({ children }) => {
  return (
    <div className='scrollbar-hide w-full max-w-[390px] bg-white m-2 rounded-xl overflow-y-scroll pb-4 shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05),0px_0px_0px_1px_rgba(10,13,18,0.05)]'>
      {children}
    </div>
  )
}

// Export the hook for components to access current device type
export const usePreviewDevice = () => useContext(PreviewContext)

export const PreviewLayout: React.FC<PreviewLayoutProps> & {
  Preview: React.FC<PreviewProps>
  Setup: React.FC<SetupProps>
} = ({ children }) => {
  const [device, setDevice] = useState<DeviceType>(DeviceType.MOBILE)

  return (
    <PreviewContext.Provider value={{ device, setDevice }}>
      <div className='flex gap-1 w-full min-h-screen max-h-dvh'>{children}</div>
    </PreviewContext.Provider>
  )
}

PreviewLayout.Preview = Preview
PreviewLayout.Setup = Setup
